import { cx, css } from '@linaria/core';
import { baseTheme } from 'styles/utils';
import { Tooltip } from 'components/PlatformResourceSelector/Tooltip';
import { IconTableCopy, IconFileCode } from 'components/Icons';
import { Link } from 'components/Link';
import { TableHeaderToolTip } from 'components/Table';
import { copyToClipboard } from 'utils/copyToClipboard';
import { CellContext } from '@tanstack/react-table';
import { Column } from 'components/Table/type';
import { formatPricingPerHour } from 'utils/pricing';
import { COST_PER_HOUR_TOOLTIP, getProviderData, formatDate, SPOT_INSTANCE_TOOLTIP, makeHardwareTemplate } from 'modules/Compute/utils';
import { Visibility, VISIBILITY_TEXT } from 'components/Visibility';
// Base interfaces for each resource type
export interface BaseTableData {
  id?: string;
  name?: string;
  provider?: JSX.Element;
  region?: JSX.Element;
  actions?: JSX.Element;
  status?: string;
  owner?: string;
  visibility?: number;
  createdAt?: string;
  modifiedAt?: string;
}
export interface ClusterTableData extends BaseTableData {
  clusterType?: string;
}
export interface NodepoolTableData extends BaseTableData {
  cluster?: string;
  hardware?: CF.API.Compute.InstanceType['compute_info'];
  autoscaling?: {
    min_instances?: number;
    max_instances?: number;
  };
  instanceType?: CF.API.Compute.InstanceType;
  minSize?: number;
  maxSize?: number;
}
export interface DeploymentTableData extends BaseTableData {
  deployment?: CF.API.Compute.Deployment;
  modelVersionId?: string;
  cluster?: string;
  clusterType?: string;
  nodepool?: Partial<CF.API.Compute.NodePool>;
  price?: CF.API.Compute.InstanceType;
  resourceType?: string;
  resourceData?: {
    id?: string;
    user_id?: string;
    app_id?: string;
  };
  autoscaling?: {
    scale_up_delay_seconds?: number;
    scale_down_delay_seconds?: number;
    scale_to_zero_delay_seconds?: number;
    traffic_history_seconds?: number;
    min_replicas?: number;
    max_replicas?: number;
    disable_packing?: boolean;
  };
}
export interface RunnerTableData extends BaseTableData {
  runner?: CF.API.Compute.Runner;
  description?: string;
  resourceType?: string;
  resourceData?: {
    id?: string;
    user_id?: string;
    app_id?: string;
  };
  worker?: CF.API.Compute.Worker;
  cluster?: CF.API.Compute.ComputeCluster;
  nodepool?: CF.API.Compute.NodePool;
  instanceType?: CF.API.Compute.InstanceType;
}

/**
 * Transforms a deployment into table-ready data.
 *
 * @remarks
 * The returned object's properties must match the accessor keys used in the table columns.
 * The 'id' field contains the full deployment object to support rich column templates.
 *
 * @param deployment - The deployment to transform
 * @returns A table-ready data object with deployment properties
 */
export const useDeploymentTableData = (deployment: CF.API.Compute.Deployment) => {
  const cluster = deployment.nodepools?.[0]?.compute_cluster;
  const nodepool = deployment.nodepools?.[0];
  const [providerData, regionData] = getProviderData(cluster?.cloud_provider?.id || '', cluster?.region || '', true);
  const modelVersionId = deployment.worker?.model?.model_version?.id || '';
  const resourceType = deployment.worker ? deployment.worker?.model ? 'Model' : 'Workflow' : '-';
  const deployConfig = deployment.autoscale_config;
  const autoScalingConfig = {
    ...deployConfig
  };
  return {
    id: deployment.id,
    deployment,
    deploymentId: deployment.id,
    modelVersionId,
    deployLatestVersion: deployment.deploy_latest_version,
    resourceType,
    resourceData: deployment.worker?.model,
    cluster,
    clusterType: cluster?.cluster_type || '',
    hardware: nodepool?.instance_types,
    nodepool: (nodepool as Partial<CF.API.Compute.NodePool>),
    price: nodepool?.instance_types?.[0],
    provider: providerData.component,
    region: regionData.component,
    createdAt: deployment.created_at,
    autoscaling: autoScalingConfig,
    visibility: deployment.visibility?.gettable
  };
};

/**
 * Transforms a cluster into table-ready data.
 *
 * @remarks
 * The returned object's properties must match the accessor keys used in the table columns.
 * The 'id' field contains the full cluster object to support rich column templates.
 *
 * @param cluster - The cluster to transform
 * @returns A table-ready data object with cluster properties
 */
export const useClusterTableData = (cluster: CF.API.Compute.ComputeCluster) => {
  if (!cluster) return {};
  const [providerData, regionData] = getProviderData(cluster.cloud_provider?.id || '', cluster.region || '', true);

  // TODO: Add nodepool and deployment counts

  return {
    id: cluster.id,
    clusterId: cluster,
    clusterType: cluster.cluster_type || '',
    status: cluster.managed_by || '',
    provider: providerData.component,
    region: regionData.component,
    createdAt: cluster.created_at,
    modifiedAt: cluster.modified_at,
    visibility: cluster.visibility?.gettable
  };
};

/**
 * Transforms a nodepool into table-ready data.
 *
 * @remarks
 * The returned object's properties must match the accessor keys used in the table columns.
 * The 'id' field contains the full nodepool object to support rich column templates.
 *
 * @param nodepool - The nodepool to transform
 * @returns A table-ready data object with nodepool properties
 */
export const useNodepoolTableData = (nodepool: CF.API.Compute.NodePool) => {
  if (!nodepool) return {};
  const cluster = nodepool.compute_cluster;
  const [providerData, regionData] = getProviderData(cluster?.cloud_provider?.id || '', cluster?.region || '', true);
  const instanceType = nodepool.instance_types?.[0];
  return {
    id: nodepool.id,
    nodepoolId: nodepool,
    cluster,
    clusterId: cluster,
    clusterType: cluster?.cluster_type || '',
    hardware: nodepool.instance_types,
    provider: providerData.component,
    region: regionData.component,
    autoscaling: {
      min_instances: nodepool.min_instances,
      max_instances: nodepool.max_instances
    },
    instanceType,
    instanceName: instanceType?.id || '',
    minSize: nodepool.min_instances || 0,
    maxSize: nodepool.max_instances || 0,
    useSpotInstances: nodepool.node_capacity_type?.capacity_types?.includes(1),
    price: instanceType,
    createdAt: nodepool.created_at,
    modifiedAt: nodepool.modified_at
  };
};

/**
 * Transforms a runner into table-ready data.
 *
 * @remarks
 * The returned object's properties must match the accessor keys used in the table columns.
 * The 'id' field contains the full runner object to support rich column templates.
 */
export const useRunnerTableData = (runner: CF.API.Compute.Runner) => {
  if (!runner) return {};
  return {
    id: runner.id,
    runnerId: runner,
    runner,
    description: runner.description,
    resourceType: runner.worker?.model ? 'Model' : 'Workflow',
    resourceData: runner.worker?.model,
    worker: runner.worker,
    cluster: runner.nodepool.compute_cluster,
    nodepool: runner.nodepool,
    instanceType: runner.nodepool.instance_types?.[0],
    createdAt: runner.created_at,
    modifiedAt: runner.modified_at,
    visibility: runner.nodepool.visibility?.gettable
  };
};
export const useComputeTableData = () => {
  return {
    useDeploymentTableData,
    useClusterTableData,
    useNodepoolTableData,
    useRunnerTableData
  };
};
type ColumnFunction<T extends BaseTableData> = (columnOverrides?: Partial<Column<T>>) => Column<T>;

// CLUSTER ID
const getClusterIdColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const showCopyButton = prop.column.columnDef?.meta?.copyable;
  const cluster = (prop.getValue() as CF.API.Compute.ComputeCluster);
  const clusterId = cluster?.id || '';
  if (!clusterId) return <span>-</span>;
  return <div className={cx(columnStyles.uniqueId)}>
      {showCopyButton && <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
          <button type="button" onClick={copyToClipboard(clusterId)}>
            <IconTableCopy size={16} />
          </button>
        </div>}
      <Link className={columnStyles.link} href={`/compute/clusters/${clusterId}`}>
        <Tooltip text={clusterId} initialState={{
        placement: 'top'
      }}>
          <div className={columnStyles.textOverflow}>{clusterId}</div>
        </Tooltip>
      </Link>
    </div>;
};

// NODEPOOL ID
const getNodepoolIdColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const showCopyButton = prop.column.columnDef?.meta?.copyable;
  const nodepool = (prop.getValue() as Partial<CF.API.Compute.NodePool>);
  const nodepoolId = nodepool?.id || '';
  const clusterId = nodepool?.compute_cluster?.id || '';
  if (!nodepoolId) return <span>-</span>;
  return <div className={cx(columnStyles.uniqueId)}>
      {showCopyButton && <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
          <button type="button" onClick={copyToClipboard(nodepoolId)}>
            <IconTableCopy size={16} />
          </button>
        </div>}
      <Link className={columnStyles.link} href={`/compute/clusters/${clusterId}/nodepools/${nodepoolId}`}>
        <Tooltip text={nodepoolId} initialState={{
        placement: 'top'
      }}>
          <div className={columnStyles.textOverflow}>{nodepoolId}</div>
        </Tooltip>
      </Link>
    </div>;
};

// DEPLOYMENT ID
const getDeploymentIdColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const showCopyButton = prop.column.columnDef?.meta?.copyable;
  const deployment = (prop?.getValue() as CF.API.Compute.Deployment);
  const deploymentId = deployment?.id;
  const clusterId = deployment?.nodepools?.[0]?.compute_cluster?.id;
  const nodePoolId = deployment?.nodepools?.[0]?.id;
  const isLocalDev = deployment?.nodepools?.[0]?.compute_cluster?.cluster_type === 'local-dev';
  const deploymentLink = `/compute/deployments/${deploymentId}/edit?computeClusterId=${clusterId}&nodePoolId=${nodePoolId}`;
  return <div className={cx(columnStyles.uniqueId)}>
      {showCopyButton && <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
          <button type="button" onClick={copyToClipboard(deploymentId)}>
            <IconTableCopy size={16} />
          </button>
        </div>}
      {!isLocalDev ? <Link className={columnStyles.link} href={deploymentLink}>
          <Tooltip text={deploymentId} initialState={{
        placement: 'top'
      }}>
            <div className={columnStyles.textOverflow}>{deploymentId}</div>
          </Tooltip>
        </Link> : <Tooltip text={deploymentId} initialState={{
      placement: 'top'
    }}>
          <div className={columnStyles.textOverflow}>{deploymentId}</div>
        </Tooltip>}
    </div>;
};

// RUNNER ID
const getRunnerIdColumnTemplate = (prop: CellContext<RunnerTableData, unknown>) => {
  const runner = (prop.getValue() as CF.API.Compute.Runner);
  const runnerId = runner?.id || '';
  return <div className={columnStyles.uniqueId}>
      <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
        <button type="button" onClick={copyToClipboard(runnerId)}>
          <IconTableCopy size={16} />
        </button>
      </div>
      <Tooltip text={runnerId} initialState={{
      placement: 'top'
    }}>
        <div className={columnStyles.textOverflow}>{runnerId}</div>
      </Tooltip>
    </div>;
};

// MODEL VERSION
const getModelVersionColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const modelVersionId = (prop.getValue() as string);
  return <div className={columnStyles.flexAlignCenter}>
      <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
        <button type="button" onClick={copyToClipboard(modelVersionId)}>
          <IconTableCopy size={16} style={{
          color: baseTheme.light.colors.gray500
        }} />
        </button>
      </div>
      <Tooltip text={modelVersionId}>
        <div className={cx(columnStyles.modelVersionId)}>
          <strong>{modelVersionId.slice(0, 5)}</strong>
          <span>{modelVersionId.slice(5, 10)}...</span>
        </div>
      </Tooltip>
    </div>;
};

// DEPLOY LATEST VERSION
const getDeployLatestVersionColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const deployLatestVersion = (prop.getValue() as boolean);
  return <div>{deployLatestVersion ? 'Yes' : 'No'}</div>;
};

// ENTITY ID
const getEntityIdColumnTemplate = (prop: CellContext<NodepoolTableData | RunnerTableData | DeploymentTableData | ClusterTableData, unknown>) => {
  const entityId = (prop.getValue() as string);
  return <div className={cx(columnStyles.uniqueId)}>
      <div className={cx(columnStyles.inlineActions, columnStyles.withButtons)}>
        <button type="button" onClick={copyToClipboard(entityId)}>
          <IconTableCopy size={16} />
        </button>
      </div>
      <Tooltip text={entityId} initialState={{
      placement: 'top'
    }}>
        <div className={columnStyles.textOverflow}>{entityId}</div>
      </Tooltip>
    </div>;
};

// ENTITY DESCRIPTION
const getEntityDescriptionColumnTemplate = (prop: CellContext<NodepoolTableData | RunnerTableData | DeploymentTableData | ClusterTableData, unknown>) => {
  const entityDescription = (prop.getValue() as string);
  return <div className={columnStyles.description}>{entityDescription}</div>;
};

// CLUSTER (FOR RESOURCE)
const getClusterColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const cluster = (prop.getValue() as CF.API.Compute.ComputeCluster);
  const clusterId = cluster?.id || '';
  if (!clusterId) return <span>-</span>;
  return <Link className={columnStyles.link} href={`/compute/clusters/${clusterId}`}>
      <Tooltip text={clusterId} initialState={{
      placement: 'top'
    }}>
        <div className={columnStyles.textOverflow}>{clusterId}</div>
      </Tooltip>
    </Link>;
};

// NODEPOOL (FOR RESOURCE)
const getNodepoolColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const nodepool = (prop.getValue() as Partial<CF.API.Compute.NodePool>);
  const clusterId = nodepool?.compute_cluster?.id || '';
  const nodepoolId = nodepool?.id || '';
  if (!nodepoolId) return <span>-</span>;
  return <Link className={columnStyles.link} href={`/compute/clusters/${clusterId}/nodepools/${nodepoolId}`}>
      <Tooltip text={nodepoolId} initialState={{
      placement: 'top'
    }}>
        <div className={columnStyles.textOverflow}>{nodepoolId}</div>
      </Tooltip>
    </Link>;
};

// CLUSTER TYPE
const getClusterTypeColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => <div className={columnStyles.clusterType}>{(prop.getValue() as string)}</div>;

// COST
const getCostColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const instance = (prop.getValue() as CF.API.Compute.InstanceType);
  const price = instance?.price;
  const costPerHour = formatPricingPerHour(Number(price));
  const costPerHourText = price !== undefined ? `$${costPerHour} / hour` : 'N/A';
  return <div className={columnStyles.cost}>{costPerHourText}</div>;
};

// INSTANCE NAME
const getInstanceNameColumnTemplate = (prop: CellContext<NodepoolTableData, unknown>) => {
  const instanceName = (prop.getValue() as string);
  return <div>{instanceName}</div>;
};

// CREATED AT
const getCreatedAtColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const date = (prop.getValue() as string);
  const formattedDate = date ? formatDate(date) : '-';
  return <span className={columnStyles.date}>{formattedDate}</span>;
};

// MODIFIED AT
const getModifiedAtColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const date = (prop.getValue() as string);
  const formattedDate = date ? formatDate(date) : '-';
  return <span className={columnStyles.date}>{formattedDate}</span>;
};

// PROVIDER
const getProviderColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => <div>{(prop.getValue() as JSX.Element)}</div>;

// REGION
const getRegionColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => <div>{(prop.getValue() as JSX.Element)}</div>;

// ACTIONS
const getActionsColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => <div className={cx(columnStyles.actionsCell, columnStyles.withButtons)}>{(prop.getValue() as JSX.Element)}</div>;

// HARDWARE
const getHardwareColumnTemplate = (prop: CellContext<NodepoolTableData, unknown>) => {
  const instances = (prop.getValue() as CF.API.Compute.InstanceType[]);
  const instance = instances?.[0]?.compute_info;
  // GPU and CPU:
  return makeHardwareTemplate({
    instance,
    fullMemory: true
  });
};

// NODEPOOL INSTANCE SCALING
const getAutoscalingColumnTemplate = (prop: CellContext<NodepoolTableData, unknown>) => {
  const autoscaling = (prop.getValue() as {
    min_instances?: number;
    max_instances?: number;
  });
  const min = autoscaling?.min_instances ?? 0;
  const max = autoscaling?.max_instances ?? 0;
  return <div>
      Min: {min} / Max: {max}
    </div>;
};

// SPOT INSTANCES
const getSpotInstancesColumnTemplate = (prop: CellContext<NodepoolTableData, unknown>) => {
  const useSpotInstances = (prop.getValue() as boolean);
  return <div>{useSpotInstances ? 'Yes' : 'No'}</div>;
};

// DEPLOY CONFIG
const getDeploymentAutoscalingColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const autoscaling = (prop.getValue() as CF.API.Compute.AutoscaleConfig);
  const tooltipStyles = {
    layout: "odules__Compute__hooks__useComputeTableColumns_tsx-layout-l1ws5sjx",
    title: "odules__Compute__hooks__useComputeTableColumns_tsx-title-tj4i8w5"
  };
  const tooltipContent = <div className={tooltipStyles.layout}>
      <span className={tooltipStyles.title}>Deployment Autoscaling Config</span>
      <p>
        <b>Min Replicas:</b> {autoscaling.min_replicas || 0}
      </p>
      <p>
        <b>Max Replicas:</b> {autoscaling.max_replicas || 0}
      </p>
      <p>
        <b>Scale Up:</b> After {autoscaling.scale_up_delay_seconds}s
      </p>
      <p>
        <b>Scale Down:</b> After {autoscaling.scale_down_delay_seconds}s
      </p>
      <p>
        <b>Scale to Zero:</b> After {autoscaling.scale_to_zero_delay_seconds}s
      </p>
      <p>
        <b>History Timeframe:</b> Every {autoscaling.traffic_history_seconds}s
      </p>
      <p>
        <b>Disable Packing:</b> {autoscaling.disable_packing ? 'Yes' : 'No'}
      </p>
    </div>;
  return <Tooltip text={tooltipContent}>
      <div style={{
      display: 'grid',
      placeItems: 'center'
    }}>
        <IconFileCode />
      </div>
    </Tooltip>;
};

// RESOURCE TYPE
const getResourceTypeColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const resourceType = (prop.getValue() as string);
  return <div>{resourceType}</div>;
};

// RESOURCE
const getResourceColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const resourceData = (prop.getValue() as {
    id: string;
    user_id: string;
    app_id: string;
  });
  const resourceType = prop.row.original.resourceType;
  if (!resourceData || !resourceType) return '-';
  const resourceTypeSlug = resourceType === 'Model' ? 'models' : 'workflows';
  const resourceLink = `/${resourceData.user_id}/${resourceData.app_id}/${resourceTypeSlug}/${resourceData.id}`;
  return <Link className={columnStyles.link} href={resourceLink}>
      <Tooltip text={resourceData.id} initialState={{
      placement: 'top'
    }}>
        <div className={columnStyles.textOverflow}>{resourceData.id}</div>
      </Tooltip>
    </Link>;
};

// VISIBILITY
const getVisibilityColumnTemplate = (prop: CellContext<DeploymentTableData, unknown>) => {
  const visibilityCode = (prop.getValue() as number);
  const visibilityType = visibilityCode === 10 ? 'PRIVATE' : 'PUBLIC';
  return <Visibility visibility={visibilityType} text={VISIBILITY_TEXT[visibilityType]} />;
};
interface ComputeTableColumns<T extends BaseTableData> {
  makeClusterIdColumn: ColumnFunction<T>;
  makeNodepoolIdColumn: ColumnFunction<T>;
  makeDeploymentIdColumn: ColumnFunction<T>;
  makeRunnerIdColumn: ColumnFunction<T>;
  makeModelVersionColumn: ColumnFunction<T>;
  makeDeployLatestVersionColumn: ColumnFunction<T>;
  makeClusterTypeColumn: ColumnFunction<T>;
  makeClusterColumn: ColumnFunction<T>;
  makeNodepoolColumn: ColumnFunction<T>;
  makeEntityIdColumn: ColumnFunction<T>;
  makeEntityDescriptionColumn: ColumnFunction<T>;
  makeInstanceNameColumn: ColumnFunction<T>;
  makeHardwareColumn: ColumnFunction<T>;
  makeAutoscalingColumn: ColumnFunction<T>;
  makeDeploymentAutoscalingColumn: ColumnFunction<T>;
  makeSpotInstancesColumn: ColumnFunction<T>;
  makeCostColumn: ColumnFunction<T>;
  makeProviderColumn: ColumnFunction<T>;
  makeRegionColumn: ColumnFunction<T>;
  makeActionsColumn: ColumnFunction<T>;
  makeCreatedAtColumn: ColumnFunction<T>;
  makeModifiedAtColumn: ColumnFunction<T>;
  makeResourceTypeColumn: ColumnFunction<T>;
  makeResourceColumn: ColumnFunction<T>;
  makeVisibilityColumn: ColumnFunction<T>;
}

/**
 * Function to create a table column with optional cell override capability.
 *
 * @remarks
 * By default, cell overrides are disabled to encourage using the data transformation pattern
 * for rendering cells. If you need to customize a cell's rendering, explicitly set allowCellOverride to true.
 *
 * Example of the preferred data transformation pattern:
 * ```typescript
 * const data = items.map(item => ({
 *   ...useItemTableData(item),
 *   actions: <ActionList name={item.id} contextMenuActions={[
 *     <Button key="edit" onClick={() => handleEdit(item)}>Edit</Button>,
 *     <DeleteButton key="delete" onDelete={() => handleDelete(item)} />
 *   ]} />
 * }));
 * ```
 */
const createColumnFunction = <T extends BaseTableData,>(defaultColumn: Column<T>, allowCellOverride: boolean = false): ColumnFunction<T> => {
  return (columnOverrides: Partial<Column<T>> = {}) => {
    const merged = ({
      ...defaultColumn,
      ...columnOverrides,
      // Only allow cell override if explicitly enabled
      cell: allowCellOverride ? columnOverrides.cell || defaultColumn.cell : defaultColumn.cell
    } as Column<T>);
    return merged;
  };
};

/**
 * Hook that provides column definitions for compute-related tables.
 *
 * @remarks
 * For action columns, use the data transformation pattern with component-based actions.
 * This means transforming your data to include the actions component rather than defining it in the column,
 * and ensuring all actions are React components (not action objects).
 *
 * Preferred pattern:
 * ```typescript
 * // In your component:
 * const data = items.map(item => {
 *   const itemActions = [
 *     <Button key="edit" onClick={() => handleEdit(item)}>Edit</Button>,
 *     <Button key="deploy" onClick={() => handleDeploy(item)}>Deploy</Button>,
 *     <DeleteButton key="delete" onDelete={() => handleDelete(item)} />
 *   ];
 *   return {
 *     ...useItemTableData(item),
 *     actions: <ActionList name={item.id} isButton={false} contextMenuActions={itemActions} />
 *   };
 * });
 *
 * // Use the default actions column
 * const columns = [
 *   // ... other columns
 *   tableColumns.makeActionsColumn()
 * ];
 * ```
 */
export const useComputeTableColumns = <T extends BaseTableData,>(): ComputeTableColumns<T> => {
  // CLUSTER ID
  const defaultClusterIdColumn: Column<T> = {
    accessorKey: 'clusterId',
    header: 'Cluster ID',
    size: 225,
    enableColumnFilter: false,
    cell: ((getClusterIdColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // NODEPOOL ID
  const defaultNodepoolIdColumn: Column<T> = {
    accessorKey: 'nodepoolId',
    header: 'Nodepool ID',
    size: 225,
    enableColumnFilter: false,
    cell: ((getNodepoolIdColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // DEPLOYMENT ID
  const defaultDeploymentIdColumn: Column<T> = {
    accessorKey: 'deployment',
    header: 'Deployment ID',
    size: 225,
    enableColumnFilter: false,
    cell: ((getDeploymentIdColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // RUNNER ID
  const defaultRunnerIdColumn: Column<T> = {
    accessorKey: 'runnerId',
    header: 'Runner ID',
    enableColumnFilter: false,
    cell: ((getRunnerIdColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // MODEL VERSION
  const defaultModelVersionColumn: Column<T> = {
    accessorKey: 'modelVersionId',
    header: 'Model Version',
    enableColumnFilter: false,
    cell: ((getModelVersionColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // DEPLOY LATEST VERSION
  const defaultDeployLatestVersionColumn: Column<T> = {
    accessorKey: 'deployLatestVersion',
    header: 'Deploy Latest',
    enableColumnFilter: false,
    cell: ((getDeployLatestVersionColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // CLUSTER TYPE
  const defaultClusterTypeColumn: Column<T> = {
    accessorKey: 'clusterType',
    header: 'Cluster Type',
    enableColumnFilter: false,
    cell: ((getClusterTypeColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // CLUSTER
  const defaultClusterColumn: Column<T> = {
    accessorKey: 'cluster',
    header: 'Cluster',
    size: 200,
    enableColumnFilter: false,
    cell: ((getClusterColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // NODEPOOL
  const defaultNodepoolColumn: Column<T> = {
    accessorKey: 'nodepool',
    header: 'Nodepool',
    size: 200,
    enableColumnFilter: false,
    cell: ((getNodepoolColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // ENTITY ID
  const defaultEntityIdColumn: Column<T> = {
    accessorKey: 'id',
    header: 'ID',
    enableColumnFilter: false,
    size: 250,
    cell: ((getEntityIdColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // ENTITY DESCRIPTION
  const defaultEntityDescriptionColumn: Column<T> = {
    accessorKey: 'description',
    header: 'Description',
    enableColumnFilter: false,
    cell: ((getEntityDescriptionColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // INSTANCE NAME
  const defaultInstanceNameColumn: Column<T> = {
    accessorKey: 'instanceName',
    header: 'Instance',
    enableColumnFilter: false,
    cell: ((getInstanceNameColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // HARDWARE
  const defaultHardwareColumn: Column<T> = {
    accessorKey: 'hardware',
    header: 'Hardware',
    enableColumnFilter: false,
    size: 250,
    cell: ((getHardwareColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // NODEPOOL AUTOSCALING
  const defaultAutoscalingColumn: Column<T> = {
    accessorKey: 'autoscaling',
    header: 'Auto Scaling',
    enableColumnFilter: false,
    cell: ((getAutoscalingColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // SPOT INSTANCES
  const defaultSpotInstancesColumn: Column<T> = {
    accessorKey: 'useSpotInstances',
    header: ((<TableHeaderToolTip text="Use Spot" tooltip={<p>{SPOT_INSTANCE_TOOLTIP}</p>} /> as unknown) as string),
    enableColumnFilter: false,
    cell: ((getSpotInstancesColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // DEPLOYMENT AUTOSCALING
  const defaultDeploymentAutoscalingColumn: Column<T> = {
    accessorKey: 'autoscaling',
    header: ((<div className={columnStyles.actionsHeader}>Config</div> as unknown) as string),
    enableColumnFilter: false,
    enableSorting: false,
    size: 70,
    cell: ((getDeploymentAutoscalingColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // COST
  const defaultCostColumn: Column<T> = {
    accessorKey: 'price',
    header: ((<TableHeaderToolTip text="Price" tooltip={<p>{COST_PER_HOUR_TOOLTIP}</p>} /> as unknown) as string),
    enableColumnFilter: false,
    meta: {
      sortMetaData: {
        sortAscLabel: 'Sort (high to low)',
        sortDescLabel: 'Sort (low to high)'
      }
    },
    cell: ((getCostColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // PROVIDER
  const defaultProviderColumn: Column<T> = {
    accessorKey: 'provider',
    header: 'Provider',
    enableColumnFilter: false,
    cell: ((getProviderColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // REGION
  const defaultRegionColumn: Column<T> = {
    accessorKey: 'region',
    header: 'Region',
    enableColumnFilter: false,
    cell: ((getRegionColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // RESOURCE TYPE
  const defaultResourceTypeColumn: Column<T> = {
    accessorKey: 'resourceType',
    header: 'Resource Type',
    enableColumnFilter: false,
    cell: ((getResourceTypeColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // RESOURCE
  const defaultResourceColumn: Column<T> = {
    accessorKey: 'resourceData',
    header: 'Resource',
    enableColumnFilter: false,
    size: 225,
    cell: ((getResourceColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // CREATED AT
  const defaultCreatedAtColumn: Column<T> = {
    accessorKey: 'createdAt',
    header: 'Created',
    enableColumnFilter: false,
    meta: {
      sortMetaData: {
        sortAscLabel: 'Sort (old to new)',
        sortDescLabel: 'Sort (new to old)'
      }
    },
    size: 150,
    cell: ((getCreatedAtColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // MODIFIED AT
  const defaultModifiedAtColumn: Column<T> = {
    accessorKey: 'modifiedAt',
    header: 'Modified',
    enableColumnFilter: false,
    meta: {
      sortMetaData: {
        sortAscLabel: 'Sort (old to new)',
        sortDescLabel: 'Sort (new to old)'
      }
    },
    size: 150,
    cell: ((getModifiedAtColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // VISIBILITY
  const defaultVisibilityColumn: Column<T> = {
    accessorKey: 'visibility',
    header: 'Visibility',
    enableColumnFilter: false,
    cell: ((getVisibilityColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };

  // ACTIONS
  const defaultActionsColumn: Column<T> = {
    accessorKey: 'actions',
    header: '',
    enableColumnFilter: false,
    enableSorting: false,
    size: 70,
    cell: ((getActionsColumnTemplate as unknown) as (props: CellContext<T, unknown>) => JSX.Element)
  };
  return {
    makeClusterIdColumn: createColumnFunction<T>(defaultClusterIdColumn, true),
    makeNodepoolIdColumn: createColumnFunction<T>(defaultNodepoolIdColumn, true),
    makeDeploymentIdColumn: createColumnFunction<T>(defaultDeploymentIdColumn, true),
    makeRunnerIdColumn: createColumnFunction<T>(defaultRunnerIdColumn, true),
    makeModelVersionColumn: createColumnFunction<T>(defaultModelVersionColumn, true),
    makeDeployLatestVersionColumn: createColumnFunction<T>(defaultDeployLatestVersionColumn, true),
    makeClusterTypeColumn: createColumnFunction<T>(defaultClusterTypeColumn, true),
    makeClusterColumn: createColumnFunction<T>(defaultClusterColumn, true),
    makeNodepoolColumn: createColumnFunction<T>(defaultNodepoolColumn, true),
    makeEntityIdColumn: createColumnFunction<T>(defaultEntityIdColumn, true),
    makeEntityDescriptionColumn: createColumnFunction<T>(defaultEntityDescriptionColumn, true),
    makeInstanceNameColumn: createColumnFunction<T>(defaultInstanceNameColumn, true),
    makeHardwareColumn: createColumnFunction<T>(defaultHardwareColumn, true),
    makeAutoscalingColumn: createColumnFunction<T>(defaultAutoscalingColumn, true),
    makeDeploymentAutoscalingColumn: createColumnFunction<T>(defaultDeploymentAutoscalingColumn, true),
    makeSpotInstancesColumn: createColumnFunction<T>(defaultSpotInstancesColumn, true),
    makeCostColumn: createColumnFunction<T>(defaultCostColumn, true),
    makeProviderColumn: createColumnFunction<T>(defaultProviderColumn, true),
    makeRegionColumn: createColumnFunction<T>(defaultRegionColumn, true),
    makeActionsColumn: createColumnFunction<T>(defaultActionsColumn),
    makeCreatedAtColumn: createColumnFunction<T>(defaultCreatedAtColumn, true),
    makeModifiedAtColumn: createColumnFunction<T>(defaultModifiedAtColumn, true),
    makeResourceTypeColumn: createColumnFunction<T>(defaultResourceTypeColumn, true),
    makeResourceColumn: createColumnFunction<T>(defaultResourceColumn, true),
    makeVisibilityColumn: createColumnFunction<T>(defaultVisibilityColumn, true)
  };
};
export const columnStyles = {
  withButtons: "odules__Compute__hooks__useComputeTableColumns_tsx-withButtons-w14sgh3w",
  inlineActions: "odules__Compute__hooks__useComputeTableColumns_tsx-inlineActions-ie1arwu",
  textOverflow: "odules__Compute__hooks__useComputeTableColumns_tsx-textOverflow-t10c7wod",
  description: "odules__Compute__hooks__useComputeTableColumns_tsx-description-d156sswx",
  actionsHeader: "odules__Compute__hooks__useComputeTableColumns_tsx-actionsHeader-a84v536",
  actionsCell: "odules__Compute__hooks__useComputeTableColumns_tsx-actionsCell-a12fwjhk",
  flexBetween: "odules__Compute__hooks__useComputeTableColumns_tsx-flexBetween-f117ux83",
  flexAlignCenter: "odules__Compute__hooks__useComputeTableColumns_tsx-flexAlignCenter-f1i4prrn",
  link: "odules__Compute__hooks__useComputeTableColumns_tsx-link-l1g7dpr2",
  uniqueId: "odules__Compute__hooks__useComputeTableColumns_tsx-uniqueId-ugcaus3",
  modelVersionId: "odules__Compute__hooks__useComputeTableColumns_tsx-modelVersionId-mmf1c1j",
  clusterType: "odules__Compute__hooks__useComputeTableColumns_tsx-clusterType-c7to7bh",
  cost: "odules__Compute__hooks__useComputeTableColumns_tsx-cost-c1qwh3ob",
  date: "odules__Compute__hooks__useComputeTableColumns_tsx-date-d1vbifyc"
};

require("./useComputeTableColumns.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./useComputeTableColumns.tsx");